/**
 * Declarative route registry — single source of truth for home routes and access.
 *
 * Migrate guards incrementally: new routes use AccessRoute; legacy guards delegate here.
 */

import type { RouteDefinition, RouteRole } from './types'

/** Default home path per effective role. */
export const HOME_BY_ROLE: Record<RouteRole, string> = {
  admin: '/admin',
  individual: '/analyze',
  household_owner: '/analyze',
  household_member: '/analyze',
  organization_owner: '/org/dashboard',
  organization_member: '/analyze',
}

export const ROUTES: RouteDefinition[] = [
  {
    id: 'home',
    path: '/',
    homeFor: ['admin', 'individual', 'organization_owner', 'organization_member'],
    allowedRoles: [
      'admin',
      'individual',
      'organization_owner',
      'organization_member',
      'household_owner',
      'household_member',
    ],
    adminBypass: true,
  },
  {
    id: 'admin',
    path: '/admin',
    homeFor: ['admin'],
    allowedRoles: ['admin'],
    adminActualOnly: true,
  },
  {
    id: 'org',
    path: '/org',
    homeFor: ['organization_owner'],
    allowedRoles: ['organization_owner', 'organization_member'],
    // Actual admins may enter org routes for QA without switching context.
    adminBypass: true,
  },
  {
    id: 'analyzer',
    path: '/analyze',
    allowedRoles: [
      'admin',
      'individual',
      'organization_owner',
      'organization_member',
      'household_owner',
      'household_member',
    ],
    adminBypass: true,
    requiresKnownUser: false,
  },
  {
    id: 'lens',
    path: '/lens',
    allowedRoles: ['admin', 'organization_owner', 'organization_member'],
    requiresKnownUser: true,
    adminBypass: true,
  },
  {
    id: 'lens-report',
    path: '/lens/reports',
    allowedRoles: [
      'admin',
      'individual',
      'organization_owner',
      'organization_member',
      'household_owner',
      'household_member',
    ],
    publicAccess: true,
    adminBypass: true,
  },
  {
    id: 'lens-pilot-report',
    path: '/lens/pilot-reports',
    allowedRoles: [
      'admin',
      'individual',
      'organization_owner',
      'organization_member',
      'household_owner',
      'household_member',
    ],
    publicAccess: true,
    adminBypass: true,
  },
  {
    id: 'growth',
    path: '/growth',
    allowedRoles: ['admin'],
    requiresKnownUser: true,
    adminBypass: true,
  },
  {
    id: 'settings',
    path: '/settings',
    allowedRoles: [
      'admin',
      'individual',
      'organization_owner',
      'organization_member',
      'household_owner',
      'household_member',
    ],
    requiresKnownUser: true,
    adminBypass: true,
  },
  {
    id: 'history',
    path: '/history',
    allowedRoles: ['admin', 'organization_owner', 'organization_member'],
    requiresKnownUser: true,
    adminBypass: true,
  },
]

export function getRouteById(id: RouteDefinition['id']): RouteDefinition | undefined {
  return ROUTES.find((route) => route.id === id)
}

export function getRouteForPath(pathname: string): RouteDefinition | undefined {
  const normalized = pathname.replace(/\/+$/, '') || '/'

  // Exact match first
  const exact = ROUTES.find((route) => route.path === normalized)
  if (exact) return exact

  // Longest prefix match (/admin/ask-lina → /admin)
  return [...ROUTES]
    .filter((route) => route.path !== '/' && normalized.startsWith(`${route.path}/`))
    .sort((a, b) => b.path.length - a.path.length)[0]
}

export function listRoutesForRole(role: RouteRole): RouteDefinition[] {
  return ROUTES.filter((route) => route.allowedRoles.includes(role))
}
